import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Home from "./pages/Home";
import ScrollToTop from "./components/ScrollToTop";
import { LanguageLinkPrefix, localizedPath } from "@/lib/language";
import { translateSegmentsToEs } from "@/lib/routes-i18n";
import MobileStickyCTA from "./components/MobileStickyCTA";



// Lazy-load non-critical UI & routes to reduce initial JS bundle
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const Index = lazy(() => import("./pages/Index"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BuildYourAdu = lazy(() => import("./pages/BuildYourAdu"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const ServiceArea = lazy(() => import("./pages/ServiceArea"));
const NeighborhoodPage = lazy(() => import("./pages/Neighborhood"));
const RoiCalculator = lazy(() => import("./pages/RoiCalculator"));
const Events = lazy(() => import("./pages/Events"));
const AduBuildersSanDiego = lazy(() => import("./pages/AduBuildersSanDiego"));
const AduServicePage = lazy(() => import("./pages/AduServicePage"));
const GarageConversionAduSanDiego = lazy(() => import("./pages/GarageConversionAduSanDiego"));
const AduCostSanDiego2026 = lazy(() => import("./pages/AduCostSanDiego2026"));
const AduFinancingSanDiego = lazy(() => import("./pages/AduFinancingSanDiego"));
const FloorPlans = lazy(() => import("./pages/FloorPlans"));
const FloorPlanDetail = lazy(() => import("./pages/FloorPlanDetail"));
const Glossary = lazy(() => import("./pages/Glossary"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const GetStartedSd = lazy(() => import("./pages/GetStartedSd"));
const GetStartedSdThankYou = lazy(() => import("./pages/GetStartedSdThankYou"));
const Book = lazy(() => import("./pages/Book"));
const GarageConversionLanding = lazy(() => import("./pages/GarageConversionLanding"));

/** Standalone ad landing pages: no shared nav, no sticky CTA, no chat widget. */
const AD_LANDING_PATHS = [
  "/get-started-sd",
  "/get-started-sd/thank-you",
  "/get-started-gc-4ps-a",
  "/get-started-garage-conversion-adu",
  "/book",
];

/** Legacy ad URL -> new landing, preserving gclid / utm / city params. */
const LegacyAdLandingRedirect = () => (
  <Navigate to={{ pathname: "/get-started-sd", search: window.location.search }} replace />
);

const SiteRoutes = ({ lang = "en" }: { lang?: "en" | "es" }) => {
  /** Route path for the active language (Spanish tree uses translated slugs). */
  const p = (path: string) =>
    lang === "es" ? translateSegmentsToEs(`/${path}`).replace(/^\//, "") : path;
  /** Absolute redirect target inside the active language tree. */
  const abs = (path: string) => localizedPath(path, lang);

  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path={p("get-started")} element={<Index />} />
      <Route path="get-estimate" element={<Navigate to={abs("/get-started")} replace />} />
      <Route path={p("projects")} element={<Projects />} />
      <Route
        path={p("projects/detached-adu-san-diego")}
        element={<Navigate to={abs("/projects/detached-adu-la-mesa")} replace />}
      />
      <Route path={`${p("projects")}/:slug`} element={<ProjectDetail />} />
      <Route path={p("build-your-adu")} element={<BuildYourAdu />} />
      <Route path="blog" element={<Blog />} />
      <Route path={p("blog/adu-cost-san-diego-2026")} element={<AduCostSanDiego2026 />} />
      <Route path="blog/:slug" element={<BlogPostPage />} />
      <Route path={p("areas")} element={<ServiceAreas />} />
      <Route path={`${p("areas")}/:slug`} element={<ServiceArea />} />
      <Route path={`${p("areas")}/:slug/:neighborhood`} element={<NeighborhoodPage />} />
      <Route path={p("roi-calculator")} element={<RoiCalculator />} />
      <Route path={p("events")} element={<Events />} />
      <Route path={p("adu-builders-san-diego")} element={<AduBuildersSanDiego />} />
      <Route path={p("garage-conversion-adu-san-diego")} element={<GarageConversionAduSanDiego />} />
      <Route path={p("detached-adu-san-diego")} element={<AduServicePage key="detached" service="detached" />} />
      <Route path={p("attached-adu-san-diego")} element={<AduServicePage key="attached" service="attached" />} />
      <Route path={p("adu-financing-san-diego")} element={<AduFinancingSanDiego />} />
      <Route path={p("floor-plans")} element={<FloorPlans />} />
      <Route path={`${p("floor-plans")}/:slug`} element={<FloorPlanDetail />} />
      <Route path="recursos/glosario" element={<Glossary />} />
      <Route path="resources/glossary" element={<Navigate to={abs("/recursos/glosario")} replace />} />
      <Route path={p("privacy-policy")} element={<PrivacyPolicy />} />
      <Route path={p("terms-of-use")} element={<TermsOfUse />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Hide the chat widget on mobile devices
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) return;
    // Hide the chat widget on standalone ad landing pages
    if (typeof window !== "undefined" && AD_LANDING_PATHS.includes(window.location.pathname.replace(/\/$/, ""))) return;
    const load = () => {
      setTimeout(() => {
        const s = document.createElement("script");
        s.src = "https://beta.leadconnectorhq.com/loader.js";
        s.setAttribute("data-resources-url", "https://beta.leadconnectorhq.com/chat-widget/loader.js");
        s.setAttribute("data-widget-id", "69988596d0b0e798a09d3254");
        document.body.appendChild(s);
      }, 3000);
    };
    if (document.readyState === "complete") load();
    else window.addEventListener("load", load, { once: true });
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <LanguageLinkPrefix />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/get-started-sd" element={<GetStartedSd />} />
            <Route path="/get-started-sd/thank-you" element={<GetStartedSdThankYou />} />
            <Route path="/get-started-gc-4ps-a" element={<LegacyAdLandingRedirect />} />
            <Route path="/book" element={<Book />} />
            <Route
              path="/get-started-garage-conversion-adu"
              element={<GarageConversionLanding />}
            />
            <Route path="/es/*" element={<SiteRoutes lang="es" />} />
            <Route path="/*" element={<SiteRoutes lang="en" />} />
          </Routes>
        </Suspense>
        <MobileStickyCTA />
      </BrowserRouter>

    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
